/**
 * Settings Embedding Dashboard
 *
 * Shows embedding coverage stats with per-source breakdown.
 * "Embed All" button with live progress, ETA, and cancel.
 * Electron-only — web gets a read-only view.
 *
 * @module @humanizer/studio/components/settings/SettingsEmbeddings
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface EmbedStatusData {
  total: number;
  embedded: number;
  unembedded: number;
  unembeddedBySource: Array<{ sourceType: string; count: number }>;
}

interface EmbedProgress {
  embedded: number;
  total: number;
  remaining: number;
  percent: number;
  etaSeconds: number;
  cancelled: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

const isElectron = typeof window !== 'undefined' && !!window.isElectron;

function formatEta(seconds: number): string {
  if (seconds <= 0) return '';
  if (seconds < 60) return `${seconds}s remaining`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m remaining`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${h}h ${m}m remaining`;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function SettingsEmbeddings() {
  const [status, setStatus] = useState<EmbedStatusData | null>(null);
  const [progress, setProgress] = useState<EmbedProgress | null>(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{
    embedded: number;
    durationMs: number;
    cancelled: boolean;
  } | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────────────────────────────────

  const loadStatus = useCallback(async () => {
    if (!isElectron || !window.electronArchive?.embedStatus) {
      setLoading(false);
      return;
    }
    try {
      const data = await window.electronArchive.embedStatus();
      setStatus(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load embedding status');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Clean up progress listener on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────

  const handleStart = useCallback(async () => {
    if (!window.electronArchive?.embedBatch || !window.electronArchive?.onEmbedProgress) return;

    setRunning(true);
    setLastResult(null);
    setError(null);

    // Subscribe to progress
    const unsub = window.electronArchive.onEmbedProgress((p) => {
      setProgress(p);
    });
    cleanupRef.current = unsub;

    try {
      const result = await window.electronArchive.embedBatch({ batchSize: 500 });
      setLastResult({
        embedded: result.embedded,
        durationMs: result.durationMs,
        cancelled: result.cancelled,
      });
      // Reload status to get fresh numbers
      await loadStatus();
    } catch (err: any) {
      setError(err.message || 'Embedding failed');
    } finally {
      setRunning(false);
      setProgress(null);
      unsub();
      cleanupRef.current = null;
    }
  }, [loadStatus]);

  const handleCancel = useCallback(async () => {
    if (!window.electronArchive?.embedCancel) return;
    await window.electronArchive.embedCancel();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="settings-section">
        <div className="settings-loading">
          <span className="settings-loading__spinner" />
        </div>
      </div>
    );
  }

  const percent = status && status.total > 0
    ? Math.round((status.embedded / status.total) * 100)
    : 0;
  const isComplete = status ? status.unembedded === 0 : false;

  return (
    <div className="settings-section">
      <div className="settings-section__header">
        <div>
          <h2 className="settings-section__title">Embeddings</h2>
          <p className="settings-section__description">
            Vector embeddings power semantic search, clustering, and the Drift tab.
            Nodes without embeddings can only be found via keyword search.
          </p>
        </div>
      </div>

      <div className="settings-section__content">
        {!isElectron && (
          <div className="settings-models__web-notice">
            Embedding management is only available in the desktop app.
          </div>
        )}

        {isElectron && status && (
          <>
            {/* Coverage card */}
            <div className="settings-embed__card">
              <div className="settings-embed__stats-row">
                <span className="settings-embed__stat-label">Coverage</span>
                <span className="settings-embed__stat-value">
                  {formatNumber(status.embedded)} / {formatNumber(status.total)} ({percent}%)
                </span>
              </div>

              <div className="settings-embed__progress-bar">
                <div
                  className={`settings-embed__progress-fill${isComplete ? ' settings-embed__progress-fill--complete' : ''}`}
                  style={{ width: `${percent}%` }}
                />
              </div>

              {isComplete && (
                <div className="settings-embed__complete-msg">
                  All nodes are embedded. Semantic search covers your entire archive.
                </div>
              )}
            </div>

            {/* Unembedded breakdown */}
            {status.unembeddedBySource.length > 0 && (
              <div className="settings-embed__card">
                <h3 className="settings-embed__card-title">Unembedded by Source</h3>
                <div className="settings-embed__source-list">
                  {status.unembeddedBySource.map((s) => (
                    <div key={s.sourceType} className="settings-embed__source-row">
                      <span className="settings-embed__source-name">{s.sourceType}</span>
                      <span className="settings-embed__source-count">{formatNumber(s.count)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Running progress */}
            {running && progress && (
              <div className="settings-embed__card settings-embed__card--active">
                <div className="settings-embed__stats-row">
                  <span className="settings-embed__stat-label">Embedding in progress...</span>
                  <span className="settings-embed__stat-value">{progress.percent}%</span>
                </div>
                <div className="settings-embed__progress-bar">
                  <div
                    className="settings-embed__progress-fill settings-embed__progress-fill--active"
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
                <div className="settings-embed__progress-detail">
                  <span>{formatNumber(progress.embedded)} embedded, {formatNumber(progress.remaining)} remaining</span>
                  <span>{formatEta(progress.etaSeconds)}</span>
                </div>
              </div>
            )}

            {/* Last result */}
            {lastResult && !running && (
              <div className="settings-embed__card">
                <div className="settings-embed__result">
                  {lastResult.cancelled ? 'Cancelled' : 'Complete'} — embedded {formatNumber(lastResult.embedded)} nodes
                  in {Math.round(lastResult.durationMs / 1000)}s
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="settings-embed__card settings-embed__card--error">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="settings-embed__actions">
              {running ? (
                <button
                  className="settings-embed__btn settings-embed__btn--cancel"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              ) : (
                <button
                  className="settings-embed__btn settings-embed__btn--primary"
                  onClick={handleStart}
                  disabled={isComplete}
                >
                  {isComplete ? 'All Embedded' : `Embed ${formatNumber(status.unembedded)} Nodes`}
                </button>
              )}
              <button
                className="settings-embed__btn settings-embed__btn--secondary"
                onClick={loadStatus}
                disabled={running}
              >
                Refresh
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
